import {
  ContentType,
  CreateBulkAccessCodesBody,
  CreateBulkFanGroupMembersBody,
  CreateBulkWishListsBody,
  CreateBulkBadgesBody,
  Job,
  JobListQuery,
  JobListResponse,
  DistributeBulkPositionsBody,
  UpdateBulkAccessCodesBody,
  AssignUserRolesBody,
  RemoveAccessCodesBody,
  AddRemoveBulkBadgesBody,
  FanGroupFansExportValidator,
  SurveyExtensionPoint,
  SeatStatus,
  WLPositionStatus,
} from '@seaters/constants';
import { api } from '../axios-instance';

export type FileAccess = 'private' | 'public-read';

export const createUploadURL = async (
  contentType: ContentType,
  key?: string,
  access: FileAccess = 'public-read'
): Promise<{
  url: string;
  key: string;
}> => {
  return api.post('/files/upload-url', {
    contentType,
    access,
    ...(key ? { key } : {}),
  });
};

export const createPrivateUploadURL = async (
  contentType: ContentType
): Promise<{
  url: string;
  key: string;
}> => {
  return api.post('/files/upload-url', {
    contentType,
    access: 'private',
  });
};

export const getDownloadURL = async (
  key: string
): Promise<{
  key: string;
  url: string;
}> => {
  return api.post('/files/download-url', { key });
};

export const getFileStatus = async (
  key: string
): Promise<{
  key: string;
  status: string;
  url?: string;
  result?: any;
  rejectedReason?: string;
}> => {
  return api.get(`/files/status?key=${key}&shouldWaitForFinalResult=true`);
};

export const createBulkWishLists = async (
  body: CreateBulkWishListsBody
): Promise<Job> => {
  return api.post('/bulk-actions/wish-lists', body);
};

export const createBulkAccessCodes = async (
  body: CreateBulkAccessCodesBody
): Promise<Job> => {
  return api.post('/bulk-actions/codes', body);
};

export const createBulkFanGroupMembers = async (
  body: CreateBulkFanGroupMembersBody
): Promise<Job> => {
  return api.post('/bulk-actions/fan-group-members', body);
};

export const createBulkBadges = async (
  body: CreateBulkBadgesBody
): Promise<Job> => {
  return api.post('/bulk-actions/badges', body);
};

export const removeBulkBadges = async (
  body: CreateBulkBadgesBody
): Promise<Job> => {
  return api.post('/bulk-actions/badges/delete', body);
};

export const distributeBulkPositions = async (
  body: DistributeBulkPositionsBody
): Promise<Job> => {
  return api.post('/bulk-actions/position-distributions', body);
};

export const updateBulkAccessCodes = async (
  body: UpdateBulkAccessCodesBody
): Promise<Job> => {
  return api.post('/bulk-actions/enhanced-access-codes-update', body);
};

export const assignUserRoles = async (
  body: AssignUserRolesBody
): Promise<Job> => {
  return api.post('/bulk-actions/roles-assignment', body);
};

export const removeAccessCodes = async (
  body: RemoveAccessCodesBody
): Promise<Job> => {
  return api.post('/bulk-actions/remove-access-codes', body);
};

export const addRemoveBulkBadges = async (
  body: AddRemoveBulkBadgesBody
): Promise<Job> => {
  return api.post('/bulk-actions/badges/add-remove', body);
};

export const generateFGReportUrl = async (body: {
  fangroupSlug: string;
  firstDate?: string;
  lastDate?: string;
}): Promise<Job> => {
  return api.post('/reports/ticket-assignments', body);
};

export const generateExtendedFGReportUrl = async (body: {
  fangroupSlug: string;
  firstDate?: string;
  lastDate?: string;
}): Promise<Job> => {
  return api.post('/reports/extended-ticket-assignments', body);
};

export const generatePDFTickets = async (body: {
  xlsxKey: string;
  inputTicketsKey: string;
}): Promise<Job> => {
  return api.post('/pdf-tickets/codes', body);
};

export const generateBulkDownload = async (body: {
  xlsxKey: string;
}): Promise<Job> => {
  return api.post('/bulk-actions/download-tickets', body);
};

export const duplicateTickets = async (body: {
  timesToDuplicate: number;
  inputTicketsKey: string;
}): Promise<Job> => {
  return api.post('/bulk-actions/duplicate-pdfs', body);
};

export const mergeTickets = async (body: {
  xlsxKey: string;
  inputTicketsKey: string;
}): Promise<Job> => {
  return api.post('/pdf-tickets/merge', body);
};

export const sortPages = async (body: {
  xlsxKey: string;
  inputPdfKey: string;
}): Promise<Job> => {
  return api.post('/pdf-tickets/sort', body);
};

// Also backs the FGO Users screen's bulk Export action (task 06): FanGroupFansExportValidator
// additionally accepts locationFilters/memberBadgeIds/query/userIds, which route the job to a
// members-search-backed filtered export instead of the legacy accepted-position export -- see
// useBulkMembersExport / bulkSelection.ts's toExportRequestFields.
export const generateFansReportUrl = async (
  body: FanGroupFansExportValidator
): Promise<Job> => {
  return api.post('/reports/fangroup-fans-export', body);
};

export const generateFullFansReportUrl = async (body: {
  fangroupSlug: string;
}): Promise<Job> => {
  return api.post('/reports/full-fangroup-fans-export', body);
};

export const generateOlympicsReportUrl = async (body: {
  fangroupSlug: string;
  ticketsXlsxKey: string;
  contactsXlsxKey: string;
  badgeIds: string[];
  startDate?: string;
}): Promise<Job> => {
  return api.post('/reports/olympics/fan-session-export', body);
};

/**
 * Starts the winners ("Export List") report as a background job and returns it straight away; the
 * file is fetched later from the job's signed URL. Replaces the Java `export-seats` endpoint, which
 * built the whole file inside the request and so was capped by CloudFront's 30s origin timeout.
 *
 * The wish list id is sent as `wishlist` because the Node API authorizes on that exact body key.
 */
export const startWinnersExport = async (body: {
  wishlist: string;
  /** Header text per report column, keyed by the column's English name. */
  columnLabels?: Record<string, string>;
}): Promise<Job> => {
  return api.post('/reports/winners-export', body);
};

export const generateSurveyReportUrl = async (body: {
  fangroupSlug: string;
  wishlistId: string;
  extensionPoint: SurveyExtensionPoint;
  positionStatus: WLPositionStatus;
  userLanguage: string;
}): Promise<Job> => {
  return api.post('/reports/survey-answers-export', body);
};
// job id: 7e173555-fc04-4ef2-a5ac-a1b7bb577ee3
export const fetchJob = async (jobId: string): Promise<Job> => {
  return api.get(`/job/${jobId}`);
};

export const fetchJobs = async (
  params: JobListQuery = {}
): Promise<JobListResponse> => {
  return api.get('/job', { params });
};

export const updateJob = async (
  jobId: string,
  body: {
    action: 'moveNext' | 'cancel' | 'retryCurrent';
  }
): Promise<Job> => {
  return api.patch(`/job/${jobId}/transitions`, body);
};

// https://api-gw.qa-seaters.com/support/api/docs#tag/job/PATCH/api/job/{id}/xlsx

export const updateJobXLSX = async (
  jobId: string,
  body: {
    key: string;
  }
): Promise<Job> => {
  return api.patch(`/job/${jobId}/xlsx`, body);
};
